/** 诉讼台账平台规则：所有企业共用，方案不得改定义。 */

import type {LitigationCase} from './litigationLedger.js';

export type LitigationCloseMode =
  | ''
  | 'judgment'
  | 'mediation'
  | 'withdrawal'
  | 'ruling'
  | 'enforcement_completed'
  | 'enforcement_suspension'
  | 'enforcement_termination'
  | 'enforcement_cancellation'
  | 'enforcement_rejection'
  | 'enforcement_refusal';
export type LitigationEnforcementStatus = 'none' | 'in_progress' | 'completed' | 'terminated';
export type LitigationLifecycleStage =
  | 'not_filed'
  | 'in_trial'
  | 'litigation_closed'
  | 'enforcement'
  | 'enforcement_terminated'
  | 'enforcement_completed';

export const CLOSE_MODE_LABELS: Record<Exclude<LitigationCloseMode, ''>, string> = {
  judgment: '判决',
  mediation: '调解',
  withdrawal: '撤诉',
  ruling: '裁定',
  enforcement_completed: '执行完毕',
  enforcement_suspension: '终结本次执行程序',
  enforcement_termination: '终结执行',
  enforcement_cancellation: '销案',
  enforcement_rejection: '驳回申请',
  enforcement_refusal: '不予执行',
};

const CLOSE_MODE_IDS = new Set<Exclude<LitigationCloseMode, ''>>(Object.keys(CLOSE_MODE_LABELS) as Array<Exclude<LitigationCloseMode, ''>>);

/**
 * 把结案方式原文或代码收成枚举。执行结案方式要先于「判决」等诉讼方式判断。
 * @param value 原文或代码
 */
export function parseCloseMode(value: unknown): LitigationCloseMode {
  const raw = String(value ?? '').trim();
  if (CLOSE_MODE_IDS.has(raw as Exclude<LitigationCloseMode, ''>)) return raw as LitigationCloseMode;
  const key = raw.replace(/\s+/g, '');
  if (!key) return '';
  if (key.includes('终结本次执行')) return 'enforcement_suspension';
  if (key.includes('不予执行')) return 'enforcement_refusal';
  if (key.includes('执行完毕')) return 'enforcement_completed';
  if (key.includes('终结执行')) return 'enforcement_termination';
  if (key.includes('销案')) return 'enforcement_cancellation';
  if (key.includes('驳回')) return 'enforcement_rejection';
  if (key.includes('调解')) return 'mediation';
  if (key.includes('撤')) return 'withdrawal';
  if (key.includes('裁定')) return 'ruling';
  if (key.includes('判决')) return 'judgment';
  return '';
}

export const ENFORCEMENT_STATUS_LABELS: Record<LitigationEnforcementStatus, string> = {
  none: '未进入执行',
  in_progress: '执行中',
  completed: '执行完毕',
  terminated: '终本执行',
};

export const LIFECYCLE_STAGE_LABELS: Record<LitigationLifecycleStage, string> = {
  not_filed: '未立案',
  in_trial: '审理中',
  litigation_closed: '诉讼结案',
  enforcement: '执行中',
  enforcement_terminated: '终本执行',
  enforcement_completed: '执行完毕',
};

/**
 * 规范化诉讼结案方式。
 * @param value 原始值
 */
export function normalizeCloseMode(value: unknown): LitigationCloseMode {
  return parseCloseMode(value);
}

/**
 * 规范化执行状态。
 * @param value 原始值
 */
export function normalizeEnforcementStatus(value: unknown): LitigationEnforcementStatus {
  return value === 'in_progress' || value === 'completed' || value === 'terminated' ? value : 'none';
}

type LifecycleFields = Pick<
  LitigationCase,
  'closed' | 'closedAt' | 'instance' | 'filingDate' | 'createdAt'
> & {
  closeMode?: LitigationCloseMode;
  enforcementStatus?: LitigationEnforcementStatus;
  litigationClosedAt?: string;
  enforcementClosedAt?: string;
};

/**
 * 补齐旧数据的诉讼/执行字段，不改变协作归属。
 * @param item 案件
 */
export function withLitigationLifecycleDefaults<T extends LifecycleFields>(item: T): T & {
  closeMode: LitigationCloseMode;
  enforcementStatus: LitigationEnforcementStatus;
  litigationClosedAt: string;
  enforcementClosedAt: string;
} {
  const closeMode = normalizeCloseMode(item.closeMode);
  let enforcementStatus = item.enforcementStatus
    ? normalizeEnforcementStatus(item.enforcementStatus)
    : item.instance === 'enforcement' && !item.closed
      ? 'in_progress'
      : 'none';
  const litigationClosedAt = String(item.litigationClosedAt || (item.closed ? item.closedAt.slice(0, 10) : '') || '');
  const enforcementClosedAt = String(item.enforcementClosedAt || '');
  if (enforcementStatus === 'none' && enforcementClosedAt) enforcementStatus = 'completed';
  return {
    ...item,
    closeMode,
    enforcementStatus,
    litigationClosedAt,
    enforcementClosedAt,
  };
}

/**
 * 平台固定生命周期。企业方案不能改这套取值。
 * @param item 案件
 */
export function litigationLifecycleStage(item: LifecycleFields): LitigationLifecycleStage {
  const current = withLitigationLifecycleDefaults(item);
  if (current.enforcementStatus === 'completed') return 'enforcement_completed';
  if (current.enforcementStatus === 'terminated') return 'enforcement_terminated';
  if (current.enforcementStatus === 'in_progress') return 'enforcement';
  if (current.closed || current.litigationClosedAt) return 'litigation_closed';
  if (!current.filingDate) return 'not_filed';
  return 'in_trial';
}

/**
 * 在审：已进入审理、尚未诉讼结案、尚未进入执行。
 * @param item 案件
 */
export function isLitigationInTrial(item: LifecycleFields): boolean {
  return litigationLifecycleStage(item) === 'in_trial';
}

/**
 * 在办：审理中、未立案、执行中或终本。执行完毕与纯诉讼结案不算在办。
 * @param item 案件
 */
export function isLitigationActiveWork(item: LifecycleFields): boolean {
  const stage = litigationLifecycleStage(item);
  return stage === 'in_trial' || stage === 'not_filed' || stage === 'enforcement' || stage === 'enforcement_terminated';
}

function inPeriod(dateValue: string, periodStart: string, periodEnd: string): boolean {
  const date = dateValue.slice(0, 10);
  return Boolean(date) && date >= periodStart && date <= periodEnd;
}

/**
 * 新收所用日期：立案日优先，空则用创建日。
 * @param item 案件
 */
export function litigationFilingDateForStats(item: Pick<LitigationCase, 'filingDate' | 'createdAt'>): string {
  return item.filingDate.slice(0, 10) || item.createdAt.slice(0, 10);
}

export type LitigationManagementStats = {
  total: number;
  amount: number;
  overdue: number;
  upcoming: number;
  filedThisPeriod: number;
  closedThisPeriod: number;
  inTrial: number;
  active: number;
  archived: number;
  periodStart: string;
  periodEnd: string;
};

/**
 * 自然年统计区间。
 * @param now 当前时间
 */
export function litigationCalendarYearPeriod(now = new Date()): {periodStart: string; periodEnd: string} {
  const year = now.getFullYear();
  return {periodStart: `${year}-01-01`, periodEnd: `${year}-12-31`};
}

/**
 * 管理报告口径。必须先完成权限过滤。
 * @param cases 可见案件
 * @param period 统计期
 */
export function summarizeLitigationManagement(
  cases: LitigationCase[],
  period = litigationCalendarYearPeriod(),
): Omit<LitigationManagementStats, 'overdue' | 'upcoming'> {
  return cases.reduce(
    (summary, item) => {
      const life = withLitigationLifecycleDefaults(item);
      summary.total += 1;
      summary.amount += Number(item.amount) || 0;
      if (inPeriod(litigationFilingDateForStats(item), period.periodStart, period.periodEnd)) summary.filedThisPeriod += 1;
      if (inPeriod(life.litigationClosedAt, period.periodStart, period.periodEnd)) summary.closedThisPeriod += 1;
      if (isLitigationInTrial(life)) summary.inTrial += 1;
      if (isLitigationActiveWork(life)) summary.active += 1;
      else summary.archived += 1;
      return summary;
    },
    {
      total: 0,
      amount: 0,
      filedThisPeriod: 0,
      closedThisPeriod: 0,
      inTrial: 0,
      active: 0,
      archived: 0,
      periodStart: period.periodStart,
      periodEnd: period.periodEnd,
    },
  );
}
